import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {AssetManufacturersOrder} from './__generated__/HydrantManufacturersPaginationQuery.graphql';
import {HydrantManufacturersTable_hydrantManufacturers$key} from './__generated__/HydrantManufacturersTable_hydrantManufacturers.graphql';
import {HydrantManufacturersTableDeleteHydrantManufacturerMutation} from './__generated__/HydrantManufacturersTableDeleteHydrantManufacturerMutation.graphql';
import {listPageHydrantManufacturersQuery} from './__generated__/listPageHydrantManufacturersQuery.graphql';
import TableFilters from './TableFilters';

type Props = {
  hydrantManufacturers: HydrantManufacturersTable_hydrantManufacturers$key;
};

type Filters = {
  name?: string;
  orderBy?: AssetManufacturersOrder;
};

function HydrantManufacturersTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPageHydrantManufacturersQuery,
    HydrantManufacturersTable_hydrantManufacturers$key
  >(
    graphql`
      fragment HydrantManufacturersTable_hydrantManufacturers on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "AssetManufacturersOrder"}
        )
        @refetchable(queryName: "HydrantManufacturersPaginationQuery") {
        hydrantManufacturers(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "HydrantManufacturersList_hydrantManufacturers") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              name
            }
          }
        }
      }
    `,
    props.hydrantManufacturers,
  );

  const [commit] = useMutation<HydrantManufacturersTableDeleteHydrantManufacturerMutation>(
    deleteHydrantManufacturerMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.hydrantManufacturers.totalCount}
      columns={columns}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      isTransitioning={isTransitioning}
      onDelete={handleDelete}
      data={data.hydrantManufacturers.edges}
    />
  );

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }

  function handleDelete(id: string) {
    commit({
      variables: {input: {hydrantManufacturerId: id}},
      onCompleted: payload => {
        const entry = payload.deleteHydrantManufacturer?.hydrantManufacturer;

        if (entry) {
          showEventNotification('removed', 'Manufacturer', entry.name);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'HydrantManufacturersList_hydrantManufacturers',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }
}

export default HydrantManufacturersTable;

const deleteHydrantManufacturerMutation = graphql`
  mutation HydrantManufacturersTableDeleteHydrantManufacturerMutation(
    $input: DeleteHydrantManufacturerInput!
  ) {
    deleteHydrantManufacturer(input: $input) {
      hydrantManufacturer {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
];
